import type { IOrganization, IUser } from "@/stores/user-store";
import { CheckOutlined, SearchOutlined } from "@ant-design/icons";
import { Input, Popover, Tooltip } from "antd";
import type { FC } from "react";
import { useMemo, useRef, useState } from "react";
import { TbArrowsExchange } from "react-icons/tb";

export interface OrgDropdownProps {
	options: IOrganization[];
	user: IUser;
	updateUserOrg: (externalFirebaseId: string, fullName: string) => void;
	compact?: boolean;
}

export const OrgDropdown: FC<OrgDropdownProps> = ({
	options,
	user,
	updateUserOrg,
	compact = false,
}) => {
	const [open, setOpen] = useState(false);
	const [searchVal, setSearchVal] = useState("");
	const searchRef = useRef<any>(null);

	const currentOrgName =
		user?.organization?.fullName ||
		user?.organization?.full_name ||
		"Organization";

	const currentOrgFirebaseId = user?.organization?.external_firebase_id;

	const sortedOptions = useMemo(() => {
		return [...(options || [])].sort((a, b) => {
			const aName = (a.fullName || a.full_name || "").toLowerCase();
			const bName = (b.fullName || b.full_name || "").toLowerCase();
			return aName > bName ? 1 : -1;
		});
	}, [options]);

	const filteredOptions = useMemo(() => {
		if (!searchVal.trim()) return sortedOptions;
		const query = searchVal.toLowerCase();
		return sortedOptions.filter((org) => {
			const name = (org.fullName || org.full_name || "").toLowerCase();
			return name.includes(query);
		});
	}, [sortedOptions, searchVal]);

	const handleSelect = (org: IOrganization) => {
		updateUserOrg(
			org.externalFirebaseId || org.external_firebase_id,
			org.fullName || org.full_name,
		);
		setOpen(false);
		setSearchVal("");
	};

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
		if (!newOpen) {
			setSearchVal("");
		} else {
			// Focus search after popover opens
			setTimeout(() => searchRef.current?.focus(), 100);
		}
	};

	const popoverContent = (
		<div className="org-popover-content">
			<div className="org-popover-header">
				<span className="org-popover-title">Switch Organization</span>
			</div>
			<div className="org-popover-search">
				<Input
					ref={searchRef}
					prefix={<SearchOutlined style={{ color: "#bbb" }} />}
					placeholder="Search..."
					value={searchVal}
					onChange={(e) => setSearchVal(e.target.value)}
					allowClear
					size="small"
				/>
			</div>
			<div className="org-popover-list">
				{filteredOptions.length === 0 ? (
					<div className="org-popover-empty">No organizations found</div>
				) : (
					filteredOptions.map((org) => {
						const orgFirebaseId = org.externalFirebaseId || org.external_firebase_id;
						const isActive = orgFirebaseId === currentOrgFirebaseId;
						const orgName = org.fullName || org.full_name;
						return (
							<div
								key={org.id}
								className={`org-popover-item ${isActive ? "active" : ""}`}
								onClick={() => handleSelect(org)}
							>
								<span className="org-popover-item-name">{orgName}</span>
								{isActive && <CheckOutlined className="org-popover-check" />}
							</div>
						);
					})
				)}
			</div>
		</div>
	);

	if (!(options && options.length)) return null;

	if (compact) {
		return (
			<Popover
				content={popoverContent}
				trigger="click"
				placement="rightBottom"
				open={open}
				onOpenChange={handleOpenChange}
				overlayClassName="org-popover-overlay"
				arrow={false}
			>
				<Tooltip
					title={currentOrgName}
					placement="right"
					mouseEnterDelay={0.1}
					overlayClassName="sidebar-tooltip"
					open={open ? false : undefined}
				>
					<button type="button" className="org-switch-btn">
						<TbArrowsExchange />
					</button>
				</Tooltip>
			</Popover>
		);
	}

	// Non-compact fallback (not currently used but kept for compatibility)
	return (
		<Popover
			content={popoverContent}
			trigger="click"
			placement="rightBottom"
			open={open}
			onOpenChange={handleOpenChange}
			overlayClassName="org-popover-overlay"
			arrow={false}
		>
			<button type="button" className="org-switch-btn-full">
				{currentOrgName}
			</button>
		</Popover>
	);
};
